import React, { useEffect, useState } from "react";
import { X, Sparkles, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function AISummaryModal({ boardId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState("");
  const [generatedAt, setGeneratedAt] = useState("");
  const [error, setError] = useState("");

  const run = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.post(`/boards/${boardId}/ai-summary`);
      setSummary(r.data.summary || "");
      setGeneratedAt(r.data.generated_at || "");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal membuat ringkasan";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ai-summary-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white border-2 border-black shadow-brutLg max-h-[90vh] overflow-hidden flex flex-col animate-blockIn">
        <div className="border-b-2 border-black p-6 bg-black text-white flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-60 mb-1">[ ASISTEN AI · GEMINI 3 FLASH ]</div>
            <h2 className="font-heading font-black text-3xl uppercase tracking-tight flex items-center gap-2">
              <Sparkles size={24} strokeWidth={2.5} /> Ringkasan Papan
            </h2>
          </div>
          <button onClick={onClose} className="w-10 h-10 border border-white bg-transparent hover:bg-white hover:text-black transition-colors" data-testid="close-ai-modal">
            <X size={16} className="mx-auto" strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="space-y-3" data-testid="ai-loading">
              <div className="font-mono text-xs uppercase tracking-widest text-black/60 animate-pulse">
                MENGANALISIS PAPAN…
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-black/10 w-3/4 animate-pulse" />
                <div className="h-3 bg-black/10 w-full animate-pulse" />
                <div className="h-3 bg-black/10 w-5/6 animate-pulse" />
                <div className="h-3 bg-black/10 w-2/3 animate-pulse" />
              </div>
            </div>
          ) : error ? (
            <div className="border-2 border-signal p-4 font-mono text-sm text-signal" data-testid="ai-error">
              {error}
            </div>
          ) : (
            <article
              data-testid="ai-summary-content"
              className="prose prose-sm max-w-none font-sans leading-relaxed
                [&_h1]:font-heading [&_h1]:font-black [&_h1]:uppercase [&_h1]:tracking-tight [&_h1]:text-2xl [&_h1]:mt-4 [&_h1]:mb-2
                [&_h2]:font-heading [&_h2]:font-bold [&_h2]:uppercase [&_h2]:tracking-tight [&_h2]:text-lg [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:border-b [&_h2]:border-black [&_h2]:pb-1
                [&_h3]:font-bold [&_h3]:uppercase [&_h3]:tracking-tight [&_h3]:text-base [&_h3]:mt-3 [&_h3]:mb-1
                [&_strong]:bg-canary [&_strong]:px-1
                [&_ul]:list-none [&_ul]:pl-0 [&_ul]:space-y-1
                [&_li]:relative [&_li]:pl-5 [&_li]:before:content-['▸'] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:text-klein [&_li]:before:font-bold
                [&_p]:mb-3 [&_p]:text-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(summary) }}
            />
          )}
        </div>

        <div className="border-t-2 border-black p-4 bg-[#F9F9F9] flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">
            {generatedAt ? `DIBUAT · ${new Date(generatedAt).toLocaleString("id-ID")}` : "—"}
          </div>
          <button
            onClick={run}
            disabled={loading}
            data-testid="regenerate-ai-btn"
            className="bg-white border-2 border-black px-4 py-2 font-mono uppercase text-xs tracking-wider hover:bg-klein hover:text-white hover:-translate-y-0.5 hover:shadow-brut transition-all flex items-center gap-2 disabled:opacity-40"
          >
            <RefreshCw size={12} strokeWidth={2.5} className={loading ? "animate-spin" : ""} /> Buat Ulang
          </button>
        </div>
      </div>
    </div>
  );
}

// Minimal markdown -> HTML (safe-ish: only renders basic syntax)
function renderMarkdown(md) {
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  // Headings
  html = html.replace(/^###\s+(.+)$/gm, "<h3>$1</h3>");
  html = html.replace(/^##\s+(.+)$/gm, "<h2>$1</h2>");
  html = html.replace(/^#\s+(.+)$/gm, "<h1>$1</h1>");
  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Italic
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
  // Bulleted lists
  html = html.replace(/(^|\n)([-*]\s+.+(?:\n[-*]\s+.+)*)/g, (m, prefix, group) => {
    const items = group.split(/\n/).map((line) => line.replace(/^[-*]\s+/, "")).map((i) => `<li>${i}</li>`).join("");
    return `${prefix}<ul>${items}</ul>`;
  });
  // Paragraphs
  html = html
    .split(/\n{2,}/)
    .map((block) => {
      if (block.match(/^<(h\d|ul|ol|pre|blockquote)/)) return block;
      return `<p>${block.replace(/\n/g, "<br/>")}</p>`;
    })
    .join("\n");
  return html;
}
